import axios from './axiosSRP';
import { getValidPhoneNumber } from '@homeplay/utils';

export const getLocalUserParams = (siteName, isLandBased) => {
  const storageKey = `${siteName ? siteName : 'hp'}-auth`;

  let userJSON = isLandBased
    ? sessionStorage.getItem(storageKey)
    : localStorage.getItem(storageKey);

  if (!userJSON) {
    return null;
  }
  try {
    const data = JSON.parse(userJSON);
    if (data.access && data.refresh) {
      return data;
    }
  } catch (e) {
    return null;
  }
  return null;
};

export const setLocalUserParams = (params, siteName, isLandBased) => {
  const storageKey = `${siteName ? siteName : 'hp'}-auth`;
  const data = JSON.stringify(params);

  isLandBased
    ? sessionStorage.setItem(storageKey, data)
    : localStorage.setItem(storageKey, data);
};

export const getValidUserTokens = async (tokens, siteName, isTouchUnit) => {
  const currentDate = Date.now();
  if (currentDate > tokens.access.expires) {
    if (currentDate < tokens.refresh.expires) {
      return await refreshToken(tokens.refresh.token, siteName, isTouchUnit);
    } else {
      return null;
    }
  }
  return tokens;
};

export const startPhoneEdit = async (accessToken, newPhone) => {
  const data = await axios.post(
    'hp/users/start_phone_edit',
    { phone: getValidPhoneNumber(newPhone) },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  return data;
};

export const confirmAuthOTP = async (phoneNumber, otpCode, siteSlug) => {
  const params = {
    phone: getValidPhoneNumber(phoneNumber),
    otp: otpCode,
    site_slug: siteSlug ? siteSlug : process.env.REACT_APP_SITE_NAME,
  };
  const { data } = await axios.post('hp/users/confirm_phone', params);
  return data.phone_token;
};

export const startRegistration = async (phoneNumber, siteName) => {
  const params = {
    phone: getValidPhoneNumber(phoneNumber),
    site_slug: siteName ? siteName : process.env.REACT_APP_SITE_NAME,
  };
  const { data } = await axios.post('hp/users/start_registration', params);
  return data;
};

export const sendAuthOTP = async (phoneNumber, siteName) => {
  const params = {
    phone: getValidPhoneNumber(phoneNumber),
    site_slug: siteName ? siteName : process.env.REACT_APP_SITE_NAME,
  };
  const { data } = await axios.post('hp/users/send_otp', params);
  return data;
};

export const createUser = async (phoneToken, password, attrs, siteName) => {
  const { data } = await axios.post('hp/users/create', {
    phone_token: phoneToken,
    password,
    first_name: attrs.firstName,
    last_name: attrs.lastName,
    site_slug: siteName ? siteName : process.env.REACT_APP_SITE_NAME,
  });
  return parseUserData(data);
};

export const loginUser = async (phoneNumber, password, siteName) => {
  const params = {
    login: getValidPhoneNumber(phoneNumber),
    password,
    provider: 'identity',
    site_slug: siteName ? siteName : process.env.REACT_APP_SITE_NAME,
  };
  const { data } = await axios.post('/hp/users/login', params);
  return parseUserData(data);
};

export const getUser = async (accessToken) => {
  const { data } = await axios.get('hp/users', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return parseUser(data.user);
};

export const updateUserData = async (accessToken, attrs) => {
  const params = {
    first_name: attrs.firstName,
    last_name: attrs.lastName,
    phone_token: attrs.phoneToken,
  };

  const { data } = await axios.post('hp/users', params, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  return parseUser(data.user);
};

export const resetPasswordRequest = async (phoneNumber, otp, siteName) => {
  const params = {
    phone: getValidPhoneNumber(phoneNumber),
    otp,
    site_slug: siteName ? siteName : process.env.REACT_APP_SITE_NAME,
  };
  const { data } = await axios.post('hp/users/password/token/create', params);
  return data;
};

export const setNewPassword = async (
  phoneNumber,
  passwordToken,
  password,
  siteName
) => {
  const params = {
    login: getValidPhoneNumber(phoneNumber),
    password_token: passwordToken,
    password,
    site_slug: siteName ? siteName : process.env.REACT_APP_SITE_NAME,
  };
  const { data } = await axios.post('hp/users/password', params);
  return data;
};

export const createBankAccount = async (accessToken, attrs) => {
  const params = {
    bank_name: attrs.bankName,
    holder_name: attrs.holderName,
    number: attrs.number,
  };
  const { data } = await axios.post(
    'hp/users/player_bank_accounts/create',
    params,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return parseUser(data.user);
};

export const startUserVerification = async (
  accessToken,
  siteName,
  requiredVerificationLevel
) => {
  const redirectUrl = `${location.origin}/${siteName ? siteName : ''}`;

  const { data } = await axios.post(
    'hp/users/kyc/start_verification',
    {
      redirect_url: redirectUrl,
      required_verification_level: requiredVerificationLevel,
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  return data.data;
};

export const getUserLastVerificationStatus = async (accessToken) => {
  const { data } = await axios.get('hp/users/kyc/get_last_verification', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  return data;
};

// NOTE: `value` is the enum key the API expects in `bank_name`; `label` is the
// display name shown to the user.
const BANKS_LIST = [
  { value: 'absa_bank', label: 'ABSA Bank' },
  { value: 'access_bank', label: 'Access Bank' },
  { value: 'african_bank', label: 'African Bank' },
  { value: 'albaraka_bank', label: 'Albaraka Bank' },
  { value: 'bank_windhoek', label: 'Bank Windhoek' },
  { value: 'sasfin_bank', label: 'Sasfin Bank' },
  { value: 'bank_zero', label: 'Bank Zero' },
  { value: 'bidvest_bank', label: 'Bidvest Bank' },
  { value: 'boe_bank', label: 'BOE Bank' },
  { value: 'capitec_bank_limited', label: 'Capitec Bank Limited' },
  { value: 'capitec_business', label: 'Capitec Business' },
  { value: 'citibank_n_a_south_africa', label: 'Citibank N.A. South Africa' },
  { value: 'discovery_bank', label: 'Discovery Bank' },
  { value: 'finbond_mutual_bank', label: 'Finbond Mutual Bank' },
  { value: 'finbond_net1', label: 'Finbond Net1' },
  { value: 'first_national_bank', label: 'First National Bank' },
  { value: 'gotyme_bank', label: 'Gotyme Bank' },
  { value: 'habib_overseas_bank_ltd', label: 'Habib Overseas Bank Ltd' },
  { value: 'hbz_bank', label: 'HBZ Bank' },
  { value: 'hsbc_bank_plc_johannesburg', label: 'HSBC Bank PLC - Johannesburg' },
  { value: 'investec_bank_limited', label: 'Investec Bank Limited' },
  { value: 'ithala_bank', label: 'Ithala Bank' },
  { value: 'jp_morgan_chase_bank', label: 'JP Morgan Chase Bank' },
  { value: 'meeg_bank', label: 'MEEG Bank' },
  { value: 'nedbank_incorp_fbc', label: 'Nedbank Incorp. FBC' },
  { value: 'nedbank_lesotho_limited', label: 'Nedbank Lesotho Limited' },
  { value: 'nedbank_limited', label: 'Nedbank Limited' },
  { value: 'nedbank_ltd_incorp_pep_bank', label: 'Nedbank Ltd Incorp. PEP Bank' },
  { value: 'olympus_mobile', label: 'Olympus Mobile' },
  { value: 'peoples_bank_ltd_inc_nbs', label: 'Peoples Bank Ltd Inc NBS' },
  { value: 'peoples_mortgage_limited', label: 'Peoples Mortgage Limited' },
  { value: 'reserve_bank', label: 'Reserve Bank' },
  { value: 'standard_bank', label: 'Standard Bank' },
  { value: 'standard_chartered_bank', label: 'Standard Chartered Bank' },
  { value: 'ubank_limited', label: 'Ubank Limited' },
  { value: 'unibank_limited', label: 'Unibank Limited' },
  { value: 'vbs_mutual_bank', label: 'VBS Mutual Bank' },
];

const BANK_LABEL_BY_VALUE = new Map(
  BANKS_LIST.map(({ value, label }) => [value, label])
);

export const getBanksList = () => BANKS_LIST;

export const getBankDisplayName = (bankName) =>
  BANK_LABEL_BY_VALUE.get(bankName) ?? bankName;

const refreshToken = async (refreshToken, siteName, isTouchUnit) => {
  const params = {
    token: refreshToken,
  };
  const { data } = await axios.post('hp/users/refresh_token', params);

  const userTokens = {
    access: { token: data.access.token, expires: data.access.expires * 1000 },
    refresh: {
      token: data.refresh.token,
      expires: data.refresh.expires * 1000,
    },
  };
  setLocalUserParams(userTokens, siteName, isTouchUnit);
  return userTokens;
};

const parseUserData = (data) => {
  return {
    user: parseUser(data.user),
    userTokens: {
      access: { token: data.access.token, expires: data.access.expires * 1000 },
      refresh: {
        token: data.refresh.token,
        expires: data.refresh.expires * 1000,
      },
    },
  };
};

const parseUser = (userData) => {
  return {
    id: userData.id,
    phone: userData.phone,
    email: userData.email,
    firstName: userData.first_name,
    lastName: userData.last_name,
    bankAccounts: getUserBankAccounts(userData.player_bank_accounts),
    verificationLevel: userData.verification_level,
  };
};

const getUserBankAccounts = (accountsList) => {
  return accountsList.map((account) => ({
    bankName: account.bank_name,
    holderName: account.holder_name,
    id: account.id,
    number: account.number,
  }));
};
